import { useHistory } from 'react-router-dom';
import { extractPathAndSearch, usePath } from '@components/Routes/usePath';
import { useServiceResolver } from '@services/useServiceResolver';
import { ULPath } from '@ul/common';
import { useAppContext } from '@contexts/app/hooks/useAppContext';

export type RouteHistoryState = {
    canGoBack: boolean;
    path: ULPath;
};

export const useNavHistory = (path: ULPath) => {
    const { useExternalRoute } = useAppContext();
    const { sdkEffect } = useServiceResolver();
    const history = useHistory<RouteHistoryState>();
    const { searchParams, pathname } = extractPathAndSearch(usePath(path));

    const goTo = (forceInternalNavigation: boolean = false): void => {
        if (useExternalRoute && !forceInternalNavigation) {
            sdkEffect.onRouteChange(path, false);
            return;
        }

        history.push({
            pathname,
            search: searchParams.toString(),
            state: {
                path,
                canGoBack: true,
            },
        });
    };

    const goToWithSearch = (passedSearchParams: URLSearchParams, isInternalOnly: boolean = false): void => {
        if (useExternalRoute && !isInternalOnly) {
            sdkEffect.onRouteChange(path, false);
            return;
        }

        history.push({
            pathname,
            search: passedSearchParams.toString(),
            state: {
                path,
                canGoBack: true,
            },
        });
    };

    const goBack = (): void => {
        if (useExternalRoute) {
            sdkEffect.onRouteChange(path, true);
            return;
        }

        if (history.location?.state?.canGoBack) {
            // Go back if coming from previous page
            history.goBack();
            return;
        }

        // Replace if page accessed directly
        history.replace({
            pathname,
            search: searchParams.toString(),
            state: {
                canGoBack: false,
                path,
            },
        });
    };

    return {
        goTo,
        goBack,
        goToWithSearch,
    };
};
